import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, tap, throwError } from 'rxjs';
import { ToastService } from '../services/toast.service';
import { MessageBuilderService } from '../services/message-builder.service';
import {
  SUPPRESS_SUCCESS_TOAST,
  SUPPRESS_ERROR_TOAST,
  CUSTOM_SUCCESS_MESSAGE,
  CUSTOM_ERROR_MESSAGE,
  ENTITY_KEY,
} from './toast.context';
import { EntityKey } from '../constants/entity-registry';

/** Entity URL patterns for automatic entity detection */
const ENTITY_PATTERNS: Array<{ pattern: RegExp; key: EntityKey }> = [
  { pattern: /\/labor-consultant/, key: 'laborConsultant' },
  { pattern: /\/cognito\/users/, key: 'employees' },
  { pattern: /\/users/, key: 'users' },
  { pattern: /\/tenants/, key: 'tenants' },
  { pattern: /\/receipts/, key: 'receipts' },
  { pattern: /\/payslips/, key: 'payslips' },
  { pattern: /\/leave-requests/, key: 'leaveRequests' },
  { pattern: /\/documents/, key: 'documents' },
  { pattern: /\/categories/, key: 'categories' },
  { pattern: /\/bacheca/, key: 'bacheca' },
  { pattern: /\/settings/, key: 'settings' },
];

/**
 * Extract entity key from URL based on registered patterns
 * @param url - Request URL
 * @returns Entity key if matched, null otherwise
 */
function extractEntityFromUrl(url: string): EntityKey | null {
  for (const { pattern, key } of ENTITY_PATTERNS) {
    if (pattern.test(url)) return key;
  }
  return null;
}

/**
 * Check if URL is for a filter/search operation (skip success toast)
 * @param url - Request URL
 * @returns True if URL is for filtering
 */
function isFilterOperation(url: string): boolean {
  return /\/(filter|search|query)/.test(url);
}

/**
 * HTTP interceptor that shows toast notifications for HTTP responses
 * Shows success toast for POST/PUT/PATCH/DELETE, error toast for all errors
 *
 * Can be customized per-request using context tokens:
 * - SUPPRESS_SUCCESS_TOAST: Skip success toast
 * - SUPPRESS_ERROR_TOAST: Skip error toast
 * - CUSTOM_SUCCESS_MESSAGE: Override success message
 * - CUSTOM_ERROR_MESSAGE: Override error message
 * - ENTITY_KEY: Specify entity for message building
 */
export const toastInterceptor: HttpInterceptorFn = (req, next) => {
  const toastService = inject(ToastService);
  const messageBuilder = inject(MessageBuilderService);

  const suppressSuccess = req.context.get(SUPPRESS_SUCCESS_TOAST);
  const suppressError = req.context.get(SUPPRESS_ERROR_TOAST);
  const customSuccessMessage = req.context.get(CUSTOM_SUCCESS_MESSAGE);
  const customErrorMessage = req.context.get(CUSTOM_ERROR_MESSAGE);
  const entityKeyOverride = req.context.get(ENTITY_KEY);

  const entityKey = entityKeyOverride ?? extractEntityFromUrl(req.url);

  return next(req).pipe(
    tap((event) => {
      if (!(event instanceof HttpResponse)) return;
      if (suppressSuccess) return;
      if (!['POST', 'PUT', 'PATCH', 'DELETE'].includes(req.method)) return;
      if (isFilterOperation(req.url)) return;

      const message = customSuccessMessage ?? messageBuilder.buildSuccessMessage(req.method, entityKey);
      toastService.success(message);
    }),
    catchError((error) => {
      if (!suppressError) {
        const message = customErrorMessage ?? messageBuilder.buildErrorMessage(error, entityKey);
        toastService.error(message);
      }

      return throwError(() => error);
    }),
  );
};
